import clsx from 'clsx';
import { lazy, Suspense } from 'react';
import { AppVersionUpdater } from '@/components/app-version-updater/app-version-updater';
import { TermsAcceptance } from '@/components/terms-acceptance/terms-acceptance';
import { useRedesign } from '@/feature-flags/redesign/use-redesign.ts';
import { NotificationsSubscription } from '@/routes/_app.p.notifications._index/notifications-subscription';
import { usePullToRefreshIos } from '@/utils/use-pull-to-refresh-ios.tsx';
import { useReadPushNotifications } from '@/utils/use-read-push-notifications.ts';
import { BootIntercom } from './boot-intercom';
import { useAppLoader } from './loader';
import { MainContent } from './main-content/main-content';
import { NotificationFaviconUpdater } from './notification-favicon-updater';
import { Sidebar } from './sidebar/sidebar';
import { useSidebarContext } from './sidebar/sidebar-context';
import { SplashScreen } from './splash-screen';
import { root, rootVariants, rootWithSidebarCollapsed } from './layout.css';

const AppShellV2 = lazy(() => import('./app-shell-v2/app-shell-v2.tsx'));

const AuthenticatedBehaviours = () => {
  usePullToRefreshIos();
  useReadPushNotifications();

  return (
    <>
      <NotificationsSubscription />
      <Suspense fallback={null}>
        <NotificationFaviconUpdater />
      </Suspense>
      <Suspense fallback={null}>
        <TermsAcceptance />
      </Suspense>
    </>
  );
};

const AppBehaviours = () => {
  const { authenticated } = useAppLoader();

  return (
    <>
      {authenticated ? <AuthenticatedBehaviours /> : null}
      <BootIntercom />
      <AppVersionUpdater />
    </>
  );
};

const AppShellV1 = () => {
  const { isCollapsed } = useSidebarContext();

  return (
    <div
      className={clsx(
        root,
        {
          [rootWithSidebarCollapsed]: isCollapsed,
        },
        rootVariants.protected,
      )}
    >
      <Sidebar />
      <MainContent />
      <AppBehaviours />
    </div>
  );
};

export const AppContent = () => {
  const redesign = useRedesign();

  if (redesign) {
    return (
      <Suspense fallback={<SplashScreen />}>
        <AppShellV2>
          <AppBehaviours />
        </AppShellV2>
      </Suspense>
    );
  }

  return <AppShellV1 />;
};
